import React, { useEffect, useState } from 'react';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Select,
  Switch,
  Flex,
  Text,
  Badge,
} from '@chakra-ui/react';

import { useColors } from './colors';

const LoadListingsScheduleModal = ({
  isOpen,
  onClose,
  initialSchedule,
  onSave,
  isSaving,
}) => {
  const { textColor, inputColor, inputBgColor } = useColors();

  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleHour, setScheduleHour] = useState(2);
  const [scheduleMinute, setScheduleMinute] = useState(0);
  const [schedulePeriod, setSchedulePeriod] = useState('AM');

  useEffect(() => {
    if (!isOpen) return;
    const sched = initialSchedule ?? {};
    setScheduleEnabled(Boolean(sched.isEnabled));
    setScheduleHour(sched.dailyTimeSchedule?.hour ?? 2);
    setScheduleMinute(sched.dailyTimeSchedule?.minute ?? 0);
    setSchedulePeriod(sched.dailyTimeSchedule?.period ?? 'AM');
  }, [isOpen, initialSchedule]);

  const handleSave = () => {
    onSave({
      isEnabled: scheduleEnabled,
      dailyTimeSchedule: {
        hour: parseInt(scheduleHour, 10),
        minute: parseInt(scheduleMinute, 10),
        period: schedulePeriod,
      },
    });
  };

  const timeLabel = `${scheduleHour}:${String(scheduleMinute).padStart(2, '0')} ${schedulePeriod}`;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius="xl">
        <ModalHeader color={textColor} pb={2}>
          Daily load schedule
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={5} align="stretch">
            <Text fontSize="sm" color="gray.600">
              Automatically fetch marketplace listings for this store on a daily schedule.
            </Text>

            <FormControl>
              <Flex justify="space-between" align="center">
                <FormLabel mb={0} color={textColor} fontSize="sm">
                  Enable scheduled load
                </FormLabel>
                <Switch
                  isChecked={scheduleEnabled}
                  onChange={(e) => setScheduleEnabled(e.target.checked)}
                  colorScheme="blue"
                />
              </Flex>
            </FormControl>

            {scheduleEnabled && (
              <Badge colorScheme="blue" alignSelf="flex-start" variant="subtle">
                Runs daily at {timeLabel}
              </Badge>
            )}

            <HStack spacing={4} align="flex-end">
              <FormControl flex={1}>
                <FormLabel fontSize="sm" color={textColor}>
                  Hour
                </FormLabel>
                <Select
                  size="md"
                  value={scheduleHour}
                  onChange={(e) => setScheduleHour(e.target.value)}
                  bg={inputBgColor}
                  color={inputColor}
                  isDisabled={!scheduleEnabled}
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
                    <option key={h} value={h}>
                      {h}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl flex={1}>
                <FormLabel fontSize="sm" color={textColor}>
                  Minute
                </FormLabel>
                <Select
                  size="md"
                  value={scheduleMinute}
                  onChange={(e) => setScheduleMinute(e.target.value)}
                  bg={inputBgColor}
                  color={inputColor}
                  isDisabled={!scheduleEnabled}
                >
                  {Array.from({ length: 60 }, (_, i) => i).map((m) => (
                    <option key={m} value={m}>
                      {m.toString().padStart(2, '0')}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl flex={1}>
                <FormLabel fontSize="sm" color={textColor}>
                  Period
                </FormLabel>
                <Select
                  size="md"
                  value={schedulePeriod}
                  onChange={(e) => setSchedulePeriod(e.target.value)}
                  bg={inputBgColor}
                  color={inputColor}
                  isDisabled={!scheduleEnabled}
                >
                  <option value="AM">AM</option>
                  <option value="PM">PM</option>
                </Select>
              </FormControl>
            </HStack>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button colorScheme="blue" onClick={handleSave} isLoading={isSaving}>
            Save schedule
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default LoadListingsScheduleModal;
